<template>
	<v-card v-if="showDataspaceCard" color="contained" @click="cardClick">
		<v-card-title>
			<span class="text-grey">
				{{ templateDataspaceTerm() }}
				>
			</span>
			<span>{{ compProps.dataspace.label }}</span>
			<v-chip :ripple="false" class="mx-2" size="small">
				{{
					compProps.dataspace.version && compProps.dataspace.version !== "wip"
						? `Release: v${compProps.dataspace.version}`
						: "WIP/Development"
				}}
			</v-chip>
			<v-chip
				prepend-icon="mdi-shield-lock"
				v-if="isRestricted"
				:ripple="false"
				size="small"
				color="warning"
				class="mx-2"
			>
				{{ symbolsStore.GUILabel("4fc7d6cf-f05a-402a-b7f5-4f728e89d0ba") }}
			</v-chip>
			<v-chip prepend-icon="mdi-phone" v-show="alternisLinked" :ripple="false" size="small" color="success">
				{{ symbolsStore.GUILabel("b4483f1b-23b0-4e38-aac9-06c38327e04f") }}
			</v-chip>
		</v-card-title>
		<v-card-subtitle v-if="compProps.dataspace.description">
			{{ compProps.dataspace.description }}
		</v-card-subtitle>
		<v-card-text>
			<v-chip-group>
				<v-chip :ripple="false">
					{{ compProps.dataspace.workspaces.length }}
					{{
						compProps.dataspace.workspaces.length > 1
							? symbolsStore.GUILabel("1f1e7900-59fa-4e7e-9926-d3bf8d0bbe4b")
							: symbolsStore.GUILabel("bf45d746-d0ca-4e20-88ea-d646d99de966")
					}}
				</v-chip>
			</v-chip-group>
			<v-btn
				v-if="domainStore.hasAccessRights(UserRole.Editor)"
				style="position: absolute; right: 10px; top: 10px"
				variant="outlined"
				icon="mdi-pencil"
				@click="editClick"
			></v-btn>
		</v-card-text>
	</v-card>
	<DataspaceEditModal
		:show="editDataspaceModal"
		:dataspace="compProps.dataspace"
		mode="edit"
		@done="handleModalDone"
		@cancel="() => (editDataspaceModal = false)"
	/>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useDomainStore } from "../../../store/domain";
import { useModelGraphStore } from "../../../store/modelGraph";
import { useBetaStore } from "../../../store/beta";
import { useSymbolsStore } from "../../../store/symbols";
import {
	type InstanceDataspace,
	type TemplateDataspace,
	type TemplateWorkspace,
} from "@lr-notitia/notitia-operator/schemas";
import DataspaceEditModal from "../components/DataspaceEditModal.vue";
import { UserRole } from "@lr-notitia/notitia-operator/schemas";
import router from "../../../router";
import { api } from "@lr-notitia/notitia-operator/client";

const domainStore = useDomainStore();
const modelStore = useModelGraphStore();
const betaStore = useBetaStore();
const symbolsStore = useSymbolsStore();

const compProps = defineProps<{
	dataspace: InstanceDataspace;
}>();

const editDataspaceModal = ref(false);
const alternisLinked = ref(true);

const showDataspaceCard = computed(() => {
	if (compProps.dataspace.id !== betaStore.kinasBetaDataspaceId) return true;
	return domainStore.isAdmin;
});

const isRestricted = ref(false);

async function checkDataspaceAccess() {
	try {
		const allowAll = await api()
			.domain(domainStore.domain.id)
			.dataspace(compProps.dataspace.id)
			.permissions()
			.getAllowAll();

		isRestricted.value = allowAll === false;
	} catch (error) {
		console.error("Error checking dataspace permissions:", error);
		isRestricted.value = false;
	}
}

function editClick(e: MouseEvent) {
	e.stopPropagation();
	editDataspaceModal.value = true;
}
function cardClick(e: MouseEvent) {
	e.stopPropagation();
	router.push("/" + domainStore.domain.id + "/template/" + compProps.dataspace.id + "/template/" + "workspaces");
}

function templateDataspaceTerm() {
	let templateDataspace = modelStore.graph.nodes[compProps.dataspace.template] as TemplateDataspace;
	if (!templateDataspace) return "Template Dataspace Not Found";
	return (
		symbolsStore.term({
			id: templateDataspace.refersTo,
			terminology: templateDataspace.terminology,
		}) || "Template Dataspace Symbol Not Found"
	);
}

async function handleModalDone() {
	editDataspaceModal.value = false;
	await checkDataspaceAccess();
}

function loadEnvironmentVariables() {
	if (!compProps.dataspace) return;
	api()
		.domain(domainStore.domain.id)
		.dataspace(compProps.dataspace.id)
		.environment()
		.getEnvironmentVariables()
		.then((data) => {
			if (Object.keys(data).includes("NTA_ALTERNIS_URL") && Object.keys(data).includes("NTA_ALTERNIS_TOKEN")) {
				alternisLinked.value = data["NTA_ALTERNIS_URL"] !== "" && data["NTA_ALTERNIS_TOKEN"] !== "";
			} else {
				alternisLinked.value = false;
			}
		})
		.catch((error) => {
			console.error("Error fetching environment variables:", error);
		});
}

onMounted(() => {
	checkDataspaceAccess();
	loadEnvironmentVariables();
});
</script>
